import React, {
  useState,
  ReactNode,
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
} from 'react';
import cx from 'clsx';
import TextareaAutosize from 'react-autosize-textarea';

import { IconInputSearch, IconRcClear } from '@/assets';

import styles from './index.module.css';

interface InputBaseProps<T> {
  disabled?: boolean;
  maxLength?: number;
  value?: string;
  defaultValue?: string;
  error?: boolean;
  className?: string;
  readOnly?: boolean;
  autoFocus?: boolean;
  onChange?(value?: string): void;
  onFocus?(e: React.FocusEvent<T>): void;
  onClick?(e: React.MouseEvent<T>): void;
  onBlur?(e: React.FocusEvent<T>): void;
  onPressEnter?(e: React.KeyboardEvent<T>): void;
  onKeyDown?(e: React.KeyboardEvent<T>): void;
  placeholder?: string;
  allowClear?: boolean;
  type?: string;
  style?: React.CSSProperties;
}

export interface InputProps extends InputBaseProps<HTMLInputElement> {
  prefix?: ReactNode;
  showLength?: boolean;
}

export const Input = forwardRef(
  (
    {
      error,
      disabled,
      maxLength,
      prefix,
      value,
      className,
      defaultValue,
      readOnly,
      autoFocus,
      onChange,
      onClick,
      onFocus,
      onBlur,
      onPressEnter,
      onKeyDown,
      placeholder,
      allowClear,
      type = 'text',
      style,
      showLength,
    }: InputProps,
    ref: React.Ref<HTMLInputElement>,
  ) => {
    const [_value, setValue] = useState(value || defaultValue);

    useLayoutEffect(() => {
      setValue(value);
    }, [value]);

    const handleKeyDown: React.KeyboardEventHandler<HTMLInputElement> = e => {
      const { keyCode } = e;
      // enter
      if (keyCode === 13 && onPressEnter) {
        onPressEnter(e);
      }
      onKeyDown?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      e.stopPropagation();
      setValue(e.target.value);
      onChange?.(e.target.value);
    };

    const handleClear = useCallback(() => {
      setValue('');
      onChange?.();
    }, [onChange]);

    const handleWheel = e => {
      // 防止滚动的时候input 数字变化
      e.target.blur();
      e.stopPropagation();
    };

    return (
      <div
        className={cx(
          styles.inputWrap,
          disabled && styles.is_disabled,
          readOnly && styles.is_readOnly,
          error && styles.is_error,
          className,
        )}
        style={style}>
        {prefix}
        <input
          ref={ref}
          autoComplete="off"
          autoFocus={autoFocus}
          onFocus={onFocus}
          onClick={onClick}
          className={styles.input}
          readOnly={readOnly}
          onBlur={onBlur}
          value={_value || ''}
          maxLength={maxLength}
          type={type}
          placeholder={placeholder}
          onChange={handleChange}
          disabled={disabled}
          onKeyDown={handleKeyDown}
          onWheel={type === 'number' ? handleWheel : undefined}
        />
        {allowClear && value && (
          <IconRcClear className={styles.clearIcon} width={16} heigh={16} onClick={handleClear} />
        )}
        {showLength && maxLength && (
          <span className={cx(styles.valueCount, _value?.length === maxLength && styles.isMax)}>
            {_value?.length || 0} / {maxLength}
          </span>
        )}
      </div>
    );
  },
);

interface TextareaProps extends InputBaseProps<HTMLTextAreaElement> {
  rows?: number;
}
interface AutoSizeTextareaProps extends TextareaProps {
  minHeight?: number;
  maxHeight?: number;
  maxRows?: number;
  showLength?: boolean;
}

export const Textarea = forwardRef(
  (
    {
      disabled,
      error,
      maxLength,
      value,
      className,
      defaultValue,
      readOnly,
      autoFocus,
      onChange,
      onClick,
      onFocus,
      onBlur,
      onPressEnter,
      onKeyDown,
      placeholder,
      rows = 4,
    }: TextareaProps,
    ref: React.Ref<HTMLTextAreaElement>,
  ) => {
    const [_value, setValue] = useState(value || defaultValue);

    useEffect(() => {
      setValue(value);
    }, [value]);

    const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = e => {
      const { keyCode } = e;
      // enter
      if (keyCode === 13 && onPressEnter) {
        onPressEnter(e);
      }
      onKeyDown?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      e.stopPropagation();
      setValue(e.target.value);
      onChange?.(e.target.value);
    };

    return (
      <div
        className={cx(
          styles.inputWrap,
          styles.textareaWrap,
          disabled && styles.is_disabled,
          readOnly && styles.is_readOnly,
          error && styles.is_error,
          className,
        )}>
        <textarea
          ref={ref}
          autoComplete="off"
          autoFocus={autoFocus}
          onFocus={onFocus}
          onClick={onClick}
          className={cx(styles.input, styles.textarea)}
          readOnly={readOnly}
          onBlur={onBlur}
          value={_value}
          maxLength={maxLength}
          placeholder={placeholder}
          onChange={handleChange}
          disabled={disabled}
          onKeyDown={handleKeyDown}
          rows={rows}
        />
      </div>
    );
  },
);

export const AutoSizeTextarea = forwardRef(
  (
    {
      disabled,
      error,
      maxLength,
      value,
      className,
      defaultValue,
      readOnly,
      autoFocus,
      onChange,
      onClick,
      onFocus,
      onBlur,
      onPressEnter,
      onKeyDown,
      placeholder,
      rows = 4,
      maxHeight,
      minHeight,
      maxRows,
      showLength,
    }: AutoSizeTextareaProps,
    ref: React.Ref<HTMLTextAreaElement>,
  ) => {
    const [_value, setValue] = useState(value || defaultValue);

    useEffect(() => {
      setValue(value);
    }, [value]);

    const handleKeyDown: React.KeyboardEventHandler<HTMLTextAreaElement> = e => {
      const { keyCode } = e;
      // enter
      if (keyCode === 13 && onPressEnter) {
        onPressEnter(e);
      }
      onKeyDown?.(e);
    };

    const handleChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
      e.stopPropagation();
      setValue(e.target.value);
      onChange?.(e.target.value);
    };

    return (
      <div
        className={cx(
          styles.inputWrap,
          styles.textareaWrap,
          disabled && styles.is_disabled,
          readOnly && styles.is_readOnly,
          error && styles.is_error,
          className,
        )}>
        {/* @ts-ignore */}
        <TextareaAutosize
          ref={ref}
          autoComplete="off"
          autoFocus={autoFocus}
          onFocus={onFocus}
          onClick={onClick}
          className={cx(styles.input, styles.textarea)}
          readOnly={readOnly}
          onBlur={onBlur}
          value={_value}
          maxRows={maxRows}
          style={{ minHeight, maxHeight }}
          maxLength={maxLength}
          placeholder={placeholder}
          onChange={handleChange}
          disabled={disabled}
          onKeyDown={handleKeyDown}
          rows={rows}
        />
        {showLength && maxLength && (
          <span className={cx(styles.maxCount, _value?.length === maxLength && styles.isMax)}>
            {_value?.length || 0} / {maxLength}
          </span>
        )}
      </div>
    );
  },
);

export interface SearchInputProps extends Omit<InputProps, 'prefix'> {
  activeClassName?: string | boolean;
  prefix?: ReactNode;
}

export const SearchInput = forwardRef(
  (
    { className, activeClassName, onBlur, onFocus, prefix, ...rest }: SearchInputProps,
    ref: React.Ref<HTMLInputElement>,
  ) => {
    const [focus, setFocus] = useState(false);

    const handleFocus = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        setFocus(true);
        onFocus?.(e);
      },
      [setFocus, onFocus],
    );

    const handleBlur = useCallback(
      (e: React.FocusEvent<HTMLInputElement>) => {
        setFocus(false);
        onBlur?.(e);
      },
      [setFocus, onBlur],
    );

    const isPrefix = typeof prefix !== 'undefined';

    return (
      // @ts-ignore
      <Input
        {...rest}
        onBlur={handleBlur}
        onFocus={handleFocus}
        className={cx(
          styles.searchInput,
          className,
          focus && (activeClassName === true ? styles.searchInputFocus : activeClassName),
        )}
        prefix={
          isPrefix ? (
            prefix
          ) : (
            <IconInputSearch width="16" height="16" className={styles.searchIcon} />
          )
        }
        ref={ref}
      />
    );
  },
);

export default Input;
